import { createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '@shared/api/client';

/**
 * Submits a feedback form via multipart/form-data to
 *   POST /accounts/{currentAccountUuid}/feedback
 *
 * Args:
 *   - currentAccountUuid: string (required)
 *   - category: 'improvement' | 'issue' | 'other'
 *   - message: string (5–2000 chars, trimmed)
 *   - attachments: File[] (0–5, validated by validateFiles upstream)
 *   - context: { pageUrl, dashboardVersion, userAgent,
 *                screenSize, clientTimestamp } (from collectContext)
 *
 * Resolves with { ok: true, submissionId }.
 * Rejects with { error, status, validationErrors?, correlationUUID? }
 * to match the existing dashboard error envelope.
 *
 * See docs/superpowers/specs/2026-05-18-feedback-mechanism-design.md §3.
 */
export const submitFeedback = createAsyncThunk(
  'feedback/submit',
  async ({ currentAccountUuid, category, message, attachments, context }, { rejectWithValue }) => {
    if (!currentAccountUuid) {
      return rejectWithValue({ error: 'No account selected', status: 400 });
    }

    const form = new FormData();
    form.append('category', category);
    form.append('message', message);
    Object.entries(context).forEach(([key, value]) => {
      form.append(key, value);
    });
    (attachments || []).forEach(file => {
      form.append('attachments', file, file.name);
    });

    try {
      const response = await client(`/accounts/${currentAccountUuid}/feedback`, {
        method: 'POST',
        body: form,
      });

      if (response.ok) {
        return { ok: true, submissionId: response.data?.submissionId };
      }

      return rejectWithValue({
        error: response.data?.message || 'Failed to send feedback',
        status: response.status,
        validationErrors: response.data?.validationErrors,
        correlationUUID: response.data?.correlationUUID,
      });
    } catch (error) {
      return rejectWithValue({
        error: error.message || 'Network error',
        status: 500,
      });
    }
  }
);
